/**
 * Run Sheet column model — order, width, visibility and locking.
 *
 * WHY THIS IS A SEPARATE MODULE
 * -----------------------------
 * The Run Sheet renders on a single shared CSS grid template (`--rs-cols`) so
 * every row's columns line up at the same X — the alignment property the sheet
 * was rebuilt around. Making columns configurable therefore means making that
 * ONE template a function of operator preference, not a constant. Keeping the
 * whole thing pure and out of the component means the interesting parts (the
 * template string, the reorder maths, the strata-noise strip) are testable
 * without rendering anything.
 *
 * OPERATOR DIRECTION (2026-07-30)
 * -------------------------------
 * - Columns are draggable (reorder), resizable (drag the header edge), and
 *   hideable.
 * - "Locked" means FROZEN SIZE AND POSITION — a padlock that protects a column
 *   from being dragged or resized. It deliberately does NOT mean "pin to the
 *   left edge": the sheet never scrolls sideways, so there is nothing to pin
 *   against, and a sticky column would buy complexity for no benefit.
 * - SP (strata plan) and QR (quote request) are first-class identifiers present
 *   on nearly every quote — 111 of 114 projects carry both — so they get real
 *   columns rather than being buried in the client string.
 */

/**
 * There is deliberately NO `actions` column (removed 2026-07-31, operator:
 * "in the main view, the buttons Resume / Open quote / Generate should not be
 * there ... they should only show when clicked and the right panel is
 * opened"). Every row control now lives in the preview panel, where the job is
 * on screen in front of you before you can act on it — the same argument that
 * had already moved Delete there. Removing the column also hands 232px back to
 * the columns that identify the job, which was the operator's standing
 * complaint about title and address clipping.
 *
 * `normaliseColumnPrefs` drops ids with no definition, so an operator carrying
 * a stored layout that still lists `actions` is reconciled silently on load.
 */
import { crmLinkedFirmName, isOwnersWording } from '../components/crm/client-record-view-model';

export type RunSheetColumnId =
    | 'select' | 'age' | 'title' | 'sp' | 'qr' | 'quoteNo' | 'label' | 'client' | 'address' | 'created' | 'respond' | 'stage' | 'quoteStatus' | 'value' | 'valueEx';

/**
 * Sort keys. `created` and `moved` are deliberately DIFFERENT things and the UI
 * must not conflate them:
 *   created — when the job first appeared. The operator's default ordering.
 *   moved   — lane-aware last-movement (see lastMovedAt), which is what drives
 *             the AGE pill's staleness alarm.
 * Sorting by `created` while showing a movement-based age is why the CREATED
 * column exists: without it the default order looks arbitrary.
 */
export type RunSheetSortKey =
    | 'created' | 'moved' | 'value' | 'valueEx' | 'title' | 'sp' | 'qr' | 'quoteNo' | 'label' | 'client' | 'address' | 'stage' | 'quoteStatus' | 'respond';

export interface RunSheetSort { key: RunSheetSortKey; dir: 'asc' | 'desc' }

/** Operator default (2026-07-30): every project, newest-created first. */
export const DEFAULT_RUN_SHEET_SORT: RunSheetSort = { key: 'created', dir: 'desc' };

/** Which column header drives which sort key. Columns absent here don't sort. */
export const COLUMN_SORT_KEY: Partial<Record<RunSheetColumnId, RunSheetSortKey>> = {
    age: 'moved', title: 'title', sp: 'sp', qr: 'qr', quoteNo: 'quoteNo', label: 'label',
    client: 'client', address: 'address', created: 'created', respond: 'respond',
    stage: 'stage', quoteStatus: 'quoteStatus', value: 'value', valueEx: 'valueEx',
};

/** Sensible first direction per key: dates and money open descending.
 *  `respond` opens ASCENDING — the useful end of a turnaround column is the
 *  fastest job, and on a mostly-empty column a descending default would put the
 *  blanks-sink rule in charge of the first screen. */
export function defaultDirFor(key: RunSheetSortKey): 'asc' | 'desc' {
    return key === 'created' || key === 'moved' || key === 'value' || key === 'valueEx' || key === 'stage' || key === 'quoteStatus' ? 'desc' : 'asc';
}

export interface SortableRow {
    createdMs?: number;
    movedMs?: number;
    /** Quote value INC GST - what the VALUE column shows. */
    value?: number;
    /** Same quote, EX GST. Its own sort key because mixed GST modes across
     *  projects mean the two orderings are not proportional. */
    valueEx?: number;
    stage?: number;
    /** Days from quote request to quote sent; null when either end is unknown. */
    respondDays?: number | null;
    title?: string;
    sp?: string;
    qr?: string;
    /** The Quote # as displayed: "RBB260905", "RBB260905 Rev 1", or another
     *  builder's number (formatQuoteNumberLabel). Sorted as text with numeric
     *  collation, so a revision sorts straight after its Rev 0. */
    quoteNo?: string;
    client?: string;
    address?: string;
    /** The operator's review label, by NAME. Deliberately not the id: sorting by
     *  a uuid would order the sheet by when a label happened to be created. */
    labelName?: string;
    /** Rank for quote progression: Sent (3), PDF made (2), Stage 5 quote built (1), draft/none (0). */
    quoteStatusRank?: number;
}

/**
 * Comparator for the run sheet.
 *
 * Blanks always sink to the bottom regardless of direction — a job with no SP
 * is not "the smallest SP", it is missing, and floating 3 empty rows to the top
 * of an SP sort would read as broken. Numeric-ish identifiers (SP, QR) compare
 * numerically when both sides are numeric so "94122" sorts below "518899"
 * instead of above it as a string would.
 */
export function compareRows(a: SortableRow, b: SortableRow, sort: RunSheetSort): number {
    const mul = sort.dir === 'asc' ? 1 : -1;
    const num = (r: SortableRow): number | null => {
        switch (sort.key) {
            case 'created': return r.createdMs ?? null;
            case 'moved': return r.movedMs ?? null;
            case 'value': return r.value ?? null;
            case 'valueEx': return r.valueEx ?? null;
            case 'stage': return r.stage ?? null;
            case 'quoteStatus': return r.quoteStatusRank ? r.quoteStatusRank : null;
            // null (not 0) when unmeasurable, so a job that has never been sent
            // sinks to the bottom under the blanks rule rather than claiming a
            // same-day turnaround.
            case 'respond': return r.respondDays ?? null;
            default: return null;
        }
    };
    const na = num(a); const nb = num(b);
    if (na !== null || nb !== null) {
        if (na === null) return 1;
        if (nb === null) return -1;
        return (na - nb) * mul;
    }

    // The one key whose sortable text does not share its name. Sorting on the
    // label ID would order the sheet by when a label happened to be created,
    // which is meaningless to the person reading it; the NAME is what is on
    // screen, so the name is what sorts. An unlabelled row therefore has empty
    // text and sinks under the blanks rule below, in both directions — which is
    // right, because "no label" is missing, not "the smallest label".
    const txt = (r: SortableRow): string => String(
        (sort.key === 'label' ? r.labelName : (r as any)[sort.key]) ?? '',
    ).trim();
    const sa = txt(a); const sb = txt(b);
    if (!sa && !sb) return 0;
    if (!sa) return 1;   // blanks sink, both directions
    if (!sb) return -1;
    if ((sort.key === 'sp' || sort.key === 'qr')) {
        const pa = Number(sa.replace(/\D/g, '')); const pb = Number(sb.replace(/\D/g, ''));
        if (Number.isFinite(pa) && Number.isFinite(pb) && pa && pb) return (pa - pb) * mul;
    }
    return sa.localeCompare(sb, 'en-AU', { sensitivity: 'base', numeric: true }) * mul;
}

/** Compact, fixed-shape date for the CREATED column: "30 Jul 26". */
export function formatCreatedDate(ms: unknown): string {
    const n = typeof ms === 'number' ? ms : Number(ms);
    if (!Number.isFinite(n) || n <= 0) return '';
    const d = new Date(n);
    if (Number.isNaN(d.getTime())) return '';
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${String(d.getFullYear() % 100).padStart(2, '0')}`;
}

export interface RunSheetColumnDef {
    id: RunSheetColumnId;
    /** Header label. Short by design: the header row costs vertical space. */
    label: string;
    /** Longer form for the config popover, where there is room to be clear. */
    longLabel: string;
    defaultWidth: number;
    minWidth: number;
    /** Structural, not a preference: always visible, never dragged or hidden. */
    system?: boolean;
    /**
     * Structural columns are PINNED to an edge, so a reorder can never strand
     * the checkbox in the middle of the data. `select` holds the left edge;
     * nothing pins right today (the `actions` column that used to is gone —
     * see the RunSheetColumnId note above), and `'end'` is kept because the
     * partition below reads it generically and the next structural column to
     * arrive should not have to reintroduce the concept.
     */
    pin?: 'start' | 'end';
    /**
     * Ships hidden, one click away in the Columns menu. Used where a column is
     * genuinely useful but the default layout has to fit a 1280px window
     * without a horizontal scrollbar — the operator asked for minimal.
     */
    defaultHidden?: boolean;
    /** Right-aligned numeric columns (value). */
    align?: 'start' | 'end';
    /**
     * Preference order for absorbing leftover width so the grid always fills
     * the sheet and no dead gutter opens on the right. Lower wins.
     */
    growPriority?: number;
}

/**
 * Default widths were picked against the real corpus, not guessed:
 * - title 300px — the longest live titles ("QUOTE RBB/GAEB (0726) - UNIT 104")
 *   clipped at the previous 218px. This is the operator's headline complaint.
 * - sp 74px / qr 84px — "SP12255" and "00197448" plus header padding.
 * - value 92px / stage 76px are unchanged from the rebuilt sheet.
 */
export const RUN_SHEET_COLUMNS: RunSheetColumnDef[] = [
    // Operator 2026-07-30: "I don't like how clicking on a row just opens that
    // project up in stage 1-5." A row click now SELECTS. Opening moved to the
    // explicit chip, which also removes a real hazard — openProject() sends a
    // finish-lane row through onLoadProject, and loading a mid-pipeline project
    // can dispatch the AI cascade, so a stray click could spend real money.
    { id: 'select', label: '', longLabel: 'Row selection', defaultWidth: 32, minWidth: 32, system: true, pin: 'start' },
    { id: 'age', label: 'AGE', longLabel: 'Age since last movement', defaultWidth: 48, minWidth: 40 },
    { id: 'title', label: 'JOB', longLabel: 'Job title', defaultWidth: 300, minWidth: 140, growPriority: 3 },
    { id: 'sp', label: 'SP', longLabel: 'Strata plan number', defaultWidth: 74, minWidth: 52 },
    { id: 'qr', label: 'QR', longLabel: 'Quote request number', defaultWidth: 84, minWidth: 58 },
    // The Quote # (docs/job/GOAL.md A5, A17): "RBB260905", "RBB260905 Rev 1",
    // or another builder's number on an imported quote. Straight after the QR
    // because the two are read together: the QR is the client's request, the
    // Quote # is which of our answers to it this row is. Several rows of one Job
    // share a QR, so this is the column that tells them apart.
    // 112px fits "RBB260905 Rev 1" (15 characters of 11px ui-monospace, about
    // 99px, plus the 10px cell gutter). It is NEVER ellipsised: below that width
    // the label wraps inside the 34px row ("RBB260905" over "Rev 1"), and the
    // 76px floor still holds "RBB260905" on one line. The cell holds three
    // 11px lines (33px), which is every RJ Bird label down to the floor and
    // another builder's number up to roughly 40 characters at this default
    // (15 or 16 monospace characters a line, fewer where a word wraps). A
    // longer one is cut at the third line rather than drawn over the rows
    // around it; its tooltip and the row's accessible name carry the whole
    // number, and widening the column shows it.
    { id: 'quoteNo', label: 'QUOTE #', longLabel: 'Quote # and revision', defaultWidth: 112, minWidth: 76 },
    // The operator's OWN mark on the job (migration 088), and the only column
    // on the sheet that is not the app's opinion about it.
    //
    // Its own column rather than a chip inside the title cell, which was the
    // other candidate: `.rs-title` is `overflow: hidden; text-overflow:
    // ellipsis`, so a chip in there is clipped per-row at a different point on
    // every row, which destroys the single-X alignment the whole sheet is built
    // around. 116px fits the names an operator actually types ("Ready to send",
    // "Needs a visit") and the chip ellipsises inside it rather than pushing.
    { id: 'label', label: 'LABEL', longLabel: 'Review label', defaultWidth: 116, minWidth: 64 },
    { id: 'client', label: 'CLIENT', longLabel: 'Client / managing agent', defaultWidth: 176, minWidth: 110, growPriority: 2 },
    { id: 'address', label: 'ADDRESS', longLabel: 'Site address', defaultWidth: 232, minWidth: 150, growPriority: 1 },
    // Exists so the DEFAULT ORDER IS LEGIBLE. The sheet sorts newest-created
    // first, while the AGE pill deliberately measures last MOVEMENT (a
    // staleness alarm, not a creation date) - so without a visible created
    // date the default ordering reads as arbitrary. Hideable like any other.
    // The quote REQUEST date (operator, 2026-07-31). Kept on the `created` id so
    // every stored layout carries over, but relabelled and shown by default,
    // because for this business those are the same event: a request arrives and
    // Rodney creates the job. Measured on the live book 2026-07-31 — 115 of 115
    // projects have created_at, and NO form_data field records a request date
    // separately, so this is the only date that can answer the question today.
    // (`intake_jobs.rfq_date` is the true RFQ date but reaches just 2 of 115
    // projects, and it is not in the summary payload — see RESPOND below.)
    { id: 'created', label: 'REQUESTED', longLabel: 'Quote request date', defaultWidth: 96, minWidth: 80 },
    // Days from request to quote sent.
    // ⚠ HONEST WARNING, measured against the live book 2026-07-31: this column
    // is BLANK on almost every row and that is a data-capture gap, not a bug
    // here. The "sent" end of the sum barely exists — email_sent_at is set on
    // 1 of 115 projects, pdf_generated_at on 0, ai_context.lastQuotePdfGeneratedAt
    // on 0, quote_status_changed_at on 3 — because Customer Zero sends quotes
    // from outside the app. The column is real and correct the moment that
    // changes; until then it shows a dash rather than inventing a number.
    { id: 'respond', label: 'RESPOND', longLabel: 'Days from request to quote sent', defaultWidth: 84, minWidth: 72, align: 'end' },
    { id: 'stage', label: 'STAGE', longLabel: 'Pipeline stage', defaultWidth: 76, minWidth: 68 },
    { id: 'quoteStatus', label: 'STATUS', longLabel: 'Quote status (PDF made / sent)', defaultWidth: 144, minWidth: 76 },
    // Operator 2026-08-08: "it needs to show the INCL. GST value but I think
    // it's showing the Excluding GST value." It was — the sheet divided by 1.1
    // and labelled the result "ex GST", while the stored scope amount is already
    // GST-inclusive on this book (projects.scope_gst_inclusive = true, and the
    // Gerard Street job stores 73410 but displayed $66,736).
    //
    // VALUE is now the inc-GST figure, which is the number on the quote the
    // client receives. The ex-GST view is not lost — it moved to its own
    // hideable column below, so both are selectable rather than one being
    // silently assumed.
    { id: 'value', label: 'VALUE', longLabel: 'Quote value inc GST', defaultWidth: 92, minWidth: 76, align: 'end' },
    // Hidden by default: most operator questions are about the number the client
    // sees. AnalysisPage still aggregates ex-GST via quoteValueExGst, so this
    // column is the bridge when the two surfaces need to be reconciled.
    { id: 'valueEx', label: 'EX GST', longLabel: 'Quote value ex GST', defaultWidth: 92, minWidth: 76, align: 'end', defaultHidden: true },
];

export interface RunSheetColumnPref {
    id: RunSheetColumnId;
    width: number;
    hidden?: boolean;
    locked?: boolean;
}

export const RUN_SHEET_COLUMNS_STORAGE_KEY = 'qm_runsheet_columns_v1';

const DEF_BY_ID = new Map(RUN_SHEET_COLUMNS.map(d => [d.id, d]));

export function getColumnDef(id: RunSheetColumnId): RunSheetColumnDef | undefined {
    return DEF_BY_ID.get(id);
}

/** The out-of-the-box layout: every column visible, at its default width. */
export function defaultColumnPrefs(): RunSheetColumnPref[] {
    return RUN_SHEET_COLUMNS.map(d => ({
        id: d.id,
        width: d.defaultWidth,
        ...(d.defaultHidden ? { hidden: true } : {}),
    }));
}

/**
 * Reconcile stored preferences against the current column set.
 *
 * Tolerant on purpose: a stored layout written by an older build must never
 * throw away the operator's arrangement, and a column added in a later build
 * must appear rather than silently vanish. Unknown ids are dropped, missing
 * ids are inserted in their canonical position (after the column that precedes
 * them in RUN_SHEET_COLUMNS), and pinned columns are forced back to their edge.
 */
export function normaliseColumnPrefs(stored: unknown): RunSheetColumnPref[] {
    const list = Array.isArray(stored) ? stored : [];
    const seen = new Set<RunSheetColumnId>();
    const out: RunSheetColumnPref[] = [];

    for (const raw of list) {
        const id = (raw && typeof raw === 'object' ? (raw as any).id : null) as RunSheetColumnId;
        const def = id ? DEF_BY_ID.get(id) : undefined;
        if (!def || seen.has(id)) continue;
        seen.add(id);
        const rawWidth = Number((raw as any).width);
        out.push({
            id,
            width: Number.isFinite(rawWidth) ? Math.max(def.minWidth, Math.round(rawWidth)) : def.defaultWidth,
            // A system column can never be hidden, whatever the stored blob says.
            hidden: def.system ? false : Boolean((raw as any).hidden),
            locked: Boolean((raw as any).locked),
        });
    }

    // Add any column the stored layout predates, in its CANONICAL position:
    // straight after the nearest column that precedes it in RUN_SHEET_COLUMNS,
    // wherever the operator has moved that one. Appending at the far right was
    // wrong for every operator with a saved layout (anyone who ever resized,
    // hid or dragged a column): QUOTE # landed after VALUE instead of beside
    // the QR it is read with, and only "Reset to defaults" fixed it. Walking
    // the canonical list in order means a missing predecessor is placed first,
    // so a run of new columns keeps its own order. No predecessor at all puts
    // the column at the front, and the pin partition below still holds `select`
    // to the left edge.
    // `defaultHidden` MUST be carried here, not just in defaultColumnPrefs():
    // an operator who already has a saved layout never goes through the defaults
    // path, so without this a new hidden-by-default column arrives VISIBLE for
    // every existing user and invisible only for brand-new ones — the opposite
    // of the intent. Caught adding the EX GST column on 2026-08-08.
    RUN_SHEET_COLUMNS.forEach((def, canonicalIndex) => {
        if (seen.has(def.id)) return;
        let at = 0;
        for (let i = canonicalIndex - 1; i >= 0; i -= 1) {
            const prevId = RUN_SHEET_COLUMNS[i]!.id;
            const pos = out.findIndex(p => p.id === prevId);
            if (pos !== -1) { at = pos + 1; break; }
        }
        out.splice(at, 0, {
            id: def.id,
            width: def.defaultWidth,
            ...(def.defaultHidden ? { hidden: true } : {}),
        });
        seen.add(def.id);
    });

    // Structural columns are pinned to their edge, whatever order the stored
    // blob (or a drag) put them in. This replaced a "find the first system
    // column and push it last" splice, which was correct only while `actions`
    // was the sole system column — it would have dragged `select` to the far
    // right the first time a stored layout was normalised.
    const pinOf = (p: RunSheetColumnPref) => DEF_BY_ID.get(p.id)?.pin;
    return [
        ...out.filter(p => pinOf(p) === 'start'),
        ...out.filter(p => !pinOf(p)),
        ...out.filter(p => pinOf(p) === 'end'),
    ];
}

/**
 * PER PERSON (Peter, 2026-10-13: "tied to the User. So that another user can
 * set up their ideal view and it won't affect other users"). The layout is
 * saved on the person's account (src/services/user-preferences.ts) and this
 * browser keeps a copy under a key of their own, so people who share a
 * computer no longer share one view. The unscoped key is the layout everyone
 * on this browser shared before: it is only READ, as a person's starting
 * layout until they have one of their own, and never written for a known
 * person. Without a user id (no session) the old shared key is used as before.
 */
export function runSheetColumnsStorageKey(userId?: string | null): string {
    return userId ? `${RUN_SHEET_COLUMNS_STORAGE_KEY}:${userId}` : RUN_SHEET_COLUMNS_STORAGE_KEY;
}

function defaultStorage(): Storage | undefined {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
}

/** The layout this browser holds for the person (or the shared one), or null when there is none. */
export function readStoredColumnPrefs(storage?: Pick<Storage, 'getItem'>, userId?: string | null): RunSheetColumnPref[] | null {
    try {
        const s = storage ?? defaultStorage();
        if (!s) return null;
        const raw = s.getItem(runSheetColumnsStorageKey(userId))
            ?? (userId ? s.getItem(RUN_SHEET_COLUMNS_STORAGE_KEY) : null);
        if (!raw) return null;
        return normaliseColumnPrefs(JSON.parse(raw));
    } catch {
        // A corrupt blob must not brick the page the operator works from.
        return null;
    }
}

export function loadColumnPrefs(storage?: Pick<Storage, 'getItem'>, userId?: string | null): RunSheetColumnPref[] {
    return readStoredColumnPrefs(storage, userId) ?? defaultColumnPrefs();
}

export function saveColumnPrefs(prefs: RunSheetColumnPref[], storage?: Pick<Storage, 'setItem'>, userId?: string | null): void {
    try {
        const s = storage ?? defaultStorage();
        s?.setItem(runSheetColumnsStorageKey(userId), JSON.stringify(prefs));
    } catch {
        /* Private mode / quota — the layout simply does not persist. */
    }
}

/**
 * AUTO-COLLAPSE (operator direction 2026-07-30)
 * ---------------------------------------------
 * The module header above promises the sheet "never scrolls sideways". It did.
 * The operator's widths are applied INLINE as `--rs-cols`, and an inline custom
 * property outranks every narrow-viewport override in dashboard-runsheet.css —
 * so those media queries were dead the moment a layout existed, and the sheet
 * side-scrolled instead of collapsing. The viewport was also the wrong thing to
 * measure: the sheet sits beside a 320px rail, so it runs out of room well
 * before a viewport breakpoint fires. This measures the SHEET and drops columns
 * in a documented order instead.
 *
 * Order is least → most important. The operator named the first three: the
 * stage dots, SP and QR go before anything that identifies the job.
 */
export const AUTO_HIDE_ORDER: RunSheetColumnId[] = [
    // `respond` goes FIRST: it is the emptiest column on the live book (the
    // sent timestamp it needs is recorded on 1 of 115 projects), so it is the
    // cheapest thing on the sheet to surrender when space runs out.
    // `label` sits AFTER the identifiers the operator named (stage/sp/qr) and
    // BEFORE the fields that say what the job IS (client/address). It is the
    // operator's own annotation, so it outranks a plan number they can look up
    // — but on a squeezed sheet, knowing WHICH job a row is still beats knowing
    // what they marked it.
    // `quoteNo` goes straight after `qr` and before `label`: it is an
    // identifier like the QR, but once several quotes share one QR it is the
    // one that tells them apart, so it outlives the QR.
    'respond', 'created', 'stage', 'quoteStatus', 'sp', 'qr', 'quoteNo', 'label', 'client', 'address', 'age',
];

/**
 * Never surrendered, at any width: what the job IS and what it is WORTH. A row
 * missing these is unusable, not merely abbreviated. (The row's controls used
 * to be a third entry here; they now live in the preview panel.)
 */
const NEVER_AUTO_HIDE = new Set<RunSheetColumnId>(['select', 'title', 'value']);

/** Floor width of a set of prefs — every track's px value is its minimum. */
function totalWidth(prefs: RunSheetColumnPref[]): number {
    return prefs.reduce((sum, p) => sum + p.width, 0);
}

/**
 * Which columns must surrender their track so the visible set fits `available`
 * px. Pure, and it NEVER mutates the operator's prefs — a column dropped here
 * reappears the moment the sheet is widened again.
 *
 * `available <= 0` means "not measured yet" and hides nothing, so first paint
 * shows the real layout rather than a briefly-collapsed one.
 */
export function autoHiddenColumns(
    prefs: RunSheetColumnPref[],
    available: number,
): Set<RunSheetColumnId> {
    const hidden = new Set<RunSheetColumnId>();
    if (!Number.isFinite(available) || available <= 0) return hidden;

    let vis = prefs.filter(p => !p.hidden && DEF_BY_ID.has(p.id));
    if (totalWidth(vis) <= available) return hidden;

    for (const id of AUTO_HIDE_ORDER) {
        if (NEVER_AUTO_HIDE.has(id)) continue;
        if (!vis.some(p => p.id === id)) continue;
        hidden.add(id);
        vis = vis.filter(p => p.id !== id);
        if (totalWidth(vis) <= available) break;
    }
    return hidden;
}

export function visibleColumns(
    prefs: RunSheetColumnPref[],
    autoHidden?: Set<RunSheetColumnId>,
): RunSheetColumnPref[] {
    return prefs.filter(p => !p.hidden && DEF_BY_ID.has(p.id) && !autoHidden?.has(p.id));
}

/**
 * Build the shared `grid-template-columns` value.
 *
 * Every column is an explicit pixel track EXCEPT one grower, which becomes
 * `minmax(width, 1fr)` so the row always spans the full sheet and no dead
 * gutter opens on the right. The grower is the highest-priority visible
 * flexible column (address, then client, then title) — so hiding the address
 * hands the slack to the client rather than stranding it.
 *
 * `collapsed` is what makes the responsive squeeze ANIMATE rather than pop
 * (operator, 2026-07-31: "make it do it nicely with a smooth animation and not
 * jerky in movement"). A collapsed column keeps its track and is emitted at
 * `0px` instead of being dropped from the list, so the track COUNT is stable
 * across the change — which is the precondition for the browser being able to
 * interpolate `grid-template-columns` at all. Drop a track instead and the two
 * lists have different lengths, the transition is skipped, and every remaining
 * column jumps sideways in one frame. Collapsed cells are still rendered (and
 * clipped to nothing by the 0px track), so the row's cell order and the
 * template can never disagree mid-transition.
 *
 * NOTE the sheet uses NO column-gap: each cell carries its own right padding
 * instead, so a 0px track really costs 0px. With a gap, a collapsed column
 * would leave its gutter behind and three collapsed columns would open 30px of
 * dead space.
 */
export function buildGridTemplate(
    prefs: RunSheetColumnPref[],
    autoHidden?: Set<RunSheetColumnId>,
    collapsed?: Set<RunSheetColumnId>,
    resizingId?: RunSheetColumnId | null,
): string {
    const vis = visibleColumns(prefs, autoHidden);
    let growId: RunSheetColumnId | null = null;
    let best = Infinity;
    for (const p of vis) {
        // A collapsed column must never be the grower: `minmax(0px, 1fr)` would
        // hand the entire slack to a column nobody can see.
        if (collapsed?.has(p.id)) continue;
        // The column currently being resized must not be the grower, or its
        // minmax(width, 1fr) track will refuse to shrink below the 1fr slack.
        if (resizingId && p.id === resizingId) continue;
        const def = DEF_BY_ID.get(p.id);
        if (!def || def.growPriority == null) continue;
        // If a flexible column was explicitly narrowed below its default width,
        // let the next flexible column absorb slack so this column stays narrow.
        if (p.width < def.defaultWidth) continue;
        if (def.growPriority < best) { best = def.growPriority; growId = p.id; }
    }
    // If all candidate growers were narrowed or skipped, fall back to the highest priority visible flexible column
    if (!growId) {
        best = Infinity;
        for (const p of vis) {
            if (collapsed?.has(p.id)) continue;
            if (resizingId && p.id === resizingId) continue;
            const def = DEF_BY_ID.get(p.id);
            if (!def || def.growPriority == null) continue;
            if (def.growPriority < best) { best = def.growPriority; growId = p.id; }
        }
    }
    return vis
        .map(p => (collapsed?.has(p.id)
            ? '0px'
            : p.id === growId ? `minmax(${p.width}px, 1fr)` : `${p.width}px`))
        .join(' ');
}

/** Move a column, refusing when either the moved or the target column is locked. */
export function reorderColumns(
    prefs: RunSheetColumnPref[],
    fromId: RunSheetColumnId,
    toId: RunSheetColumnId,
): RunSheetColumnPref[] {
    const from = prefs.findIndex(p => p.id === fromId);
    const to = prefs.findIndex(p => p.id === toId);
    if (from < 0 || to < 0 || from === to) return prefs;
    const moving = prefs[from];
    const target = prefs[to];
    if (!moving || !target) return prefs;
    if (moving.locked || target.locked) return prefs;
    if (DEF_BY_ID.get(moving.id)?.system || DEF_BY_ID.get(target.id)?.system) return prefs;
    const next = prefs.slice();
    next.splice(from, 1);
    next.splice(to, 0, moving);
    return next;
}

/** Resize a column, clamped to its floor. Locked and system columns refuse. */
export function resizeColumn(
    prefs: RunSheetColumnPref[],
    id: RunSheetColumnId,
    width: number,
): RunSheetColumnPref[] {
    const def = DEF_BY_ID.get(id);
    if (!def) return prefs;
    return prefs.map(p => {
        if (p.id !== id) return p;
        if (p.locked) return p;
        return { ...p, width: Math.max(def.minWidth, Math.round(width)) };
    });
}

/** Reset a column's width back to its default, refusing when locked or system. */
export function resetColumnWidth(
    prefs: RunSheetColumnPref[],
    id: RunSheetColumnId,
): RunSheetColumnPref[] {
    const def = DEF_BY_ID.get(id);
    if (!def || def.system) return prefs;
    return prefs.map(p => {
        if (p.id !== id) return p;
        if (p.locked) return p;
        return { ...p, width: def.defaultWidth };
    });
}

export function toggleColumnHidden(prefs: RunSheetColumnPref[], id: RunSheetColumnId): RunSheetColumnPref[] {
    return prefs.map(p => {
        if (p.id !== id) return p;
        if (DEF_BY_ID.get(p.id)?.system) return p;
        return { ...p, hidden: !p.hidden };
    });
}

export function toggleColumnLocked(prefs: RunSheetColumnPref[], id: RunSheetColumnId): RunSheetColumnPref[] {
    return prefs.map(p => (p.id === id ? { ...p, locked: !p.locked } : p));
}

export function canDragColumn(prefs: RunSheetColumnPref[], id: RunSheetColumnId): boolean {
    const p = prefs.find(x => x.id === id);
    return Boolean(p) && !p!.locked && !DEF_BY_ID.get(id)?.system;
}

// ─── Field extraction ────────────────────────────────────────────────────

/**
 * Strata plan, for display. Source values are inconsistent in the live corpus
 * ("SP12255", "SP 3460", "SP83042"), so the SP prefix is stripped and the bare
 * number shown under an "SP" header — one format, tabular, scannable.
 * Anything that is not a recognisable SP number is passed through untouched
 * rather than mangled.
 */
export function formatStrataPlan(raw: unknown): string {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    const m = s.match(/^SP[\s.:#-]*(\d{2,})$/i);
    return m && m[1] ? m[1] : s;
}

/** Quote request number. Passed through verbatim: leading zeros are meaningful. */
export function formatQuoteRef(raw: unknown): string {
    return String(raw ?? '').trim();
}

/**
 * Remove a strata-plan reference from a client string, now that SP has its own
 * column. Operator direction 2026-07-30: "show just the managing agent".
 *
 * The live corpus embeds the plan in at least six shapes:
 *   "Body Corp / Strata Plan SP62758"          -> "Body Corp"
 *   "The Owners - Strata Plan 16844"           -> "The Owners"
 *   "Owners Corporation SP6277"                -> "Owners Corporation"
 *   "SP 8509: PICA Group"                      -> "PICA Group"
 *   "Owners Corporation, Strata Plan No : 14389" -> "Owners Corporation"
 *   "Strata Plan 20423"                        -> ""   (nothing but the plan)
 *
 * A DIGIT IS REQUIRED. That is the load-bearing rule: "Waratah Strata
 * Management Pty Ltd" and "Strata Partners" are real company names that must
 * survive untouched, and they would not if the word "Strata" alone triggered
 * a strip.
 */
export function stripStrataNoise(client: unknown): string {
    let s = String(client ?? '').trim();
    if (!s) return '';

    // "Strata Plan [No|Number] [:.#-] [SP] 12345"  (digit mandatory)
    s = s.replace(/\bstrata\s*plan\b\s*(?:num(?:ber)?|no\.?)?\s*[:.#-]*\s*(?:sp)?\s*\d+\s*/gi, ' ');
    // A bare "SP12345" / "SP 12345" token (digit mandatory).
    s = s.replace(/\bsp[\s.:#-]*\d{2,}\b\s*/gi, ' ');

    // Tidy the separators the removal left behind, e.g. "Body Corp /  " or
    // ",  PICA Group" or "The Owners--".
    s = s.replace(/\s+/g, ' ')
        .replace(/^[\s,;:/|\-–—]+/, '')
        .replace(/[\s,;:/|\-–—]+$/, '')
        .trim();

    return s;
}

/**
 * The firm a job is for (SPEC 2026-10-08 D1, D5): the linked client's name
 * first (`company_name`, joined by the summary list), then the requester
 * company, then the client company unless it names the owners of a building.
 * "The Owners of Strata Plan" is what a strata quote PRINTS, never who the
 * client is. A job still linked to a building row (until Tidy up moves it to
 * the firm) falls through the same way: crmLinkedFirmName refuses a linked
 * name that is a building by its type, its plan or its owners wording, not
 * only by its wording. '' when the job names no firm.
 */
export function runSheetFirmName(project: any): string {
    const fd = project?.formData || project?.form_data || {};
    const firm = (value: unknown) => {
        const name = typeof value === 'string' ? value.trim() : '';
        return name && !isOwnersWording(name) ? name : '';
    };
    return crmLinkedFirmName(project)
        || firm(fd.requesterCompany)
        || firm(fd.clientCompany);
}

/** The Run Sheet's client column: the firm, else the person who asked. */
export function runSheetClientName(project: any): string {
    const fd = project?.formData || project?.form_data || {};
    return runSheetFirmName(project) || (typeof fd.clientName === 'string' ? fd.clientName.trim() : '');
}

// ─── Job grouping (docs/job/GOAL.md C4, D3, A17) ────────────────────────────
//
// The sheet stays ONE ROW PER PROJECT, and one project is one quote. A Job is
// only a caption placed above the quotes that share it, so every count, total,
// lane and selection the sheet already computes stays exactly what it was. The
// grouping is a re-ORDER of the rows the filters and the sort already chose,
// never a change to WHICH rows those are.

/** The fields the grouping reads off a Run Sheet row. */
export interface JobGroupableRow {
    id: string;
    lane: string;
    /** `projects.quote_job_id`. Null or empty means "a Job of one". */
    quoteJobId?: string | null;
    /** The Job's own title (`quote_jobs.title`), when the summary carries it. */
    quoteJobTitle?: string;
    /** The Job's QR and SP, already formatted like the QR and SP columns. */
    jobQr?: string;
    jobSp?: string;
    /** The quote's own title, QR and SP (fallbacks for the caption). */
    title?: string;
    qr?: string;
    sp?: string;
}

export interface RunSheetJobCaption {
    jobId: string;
    /** Job title, falling back to the first quote's title without its area. */
    title: string;
    qr: string;
    sp: string;
    /** Quotes of this Job shown in the current view (always 2 or more). */
    count: number;
    /** Quotes of this Job sitting in a lane the current view does not show. */
    inOtherLanes: number;
    /** Quotes of this Job in a shown lane but hidden by the search or a filter. */
    hiddenByFilters: number;
}

export interface RunSheetDisplay<T> {
    /** The same rows as the input, in display order. */
    rows: T[];
    /** Keyed by the id of the row the caption sits directly above. */
    captions: Map<string, RunSheetJobCaption>;
    /** The last row of each caption's group, so the group can be closed. */
    lastMemberIds: Set<string>;
    /** Every row that sits under a caption. */
    memberIds: Set<string>;
    /**
     * For a row under a caption whose title starts with that caption's Job
     * title: the rest of its title (the area, "Roof"), which is what tells the
     * Job's quotes apart. Rows whose title adds nothing to the Job's are absent.
     */
    memberTitles: Map<string, string>;
}

/**
 * A Job title from one of its quote titles. A split quote is titled
 * "<job title> - <area>" (D2), so the last " - area" segment is dropped:
 * "RBB/MRM (0926) - Balcony" gives "RBB/MRM (0926)". Word's AutoFormat turns
 * " - " into an en or em dash in imported text, so those separate too. A title
 * with no such segment comes back unchanged, as does one that is nothing but
 * the segment.
 */
// Hyphen, en dash, em dash. Built from char codes so no dash glyph sits in
// this source file.
const TITLE_DASHES = '-' + String.fromCharCode(0x2013, 0x2014);
const AREA_SUFFIX = new RegExp(`^(.*\\S)\\s+[${TITLE_DASHES}]\\s+[^${TITLE_DASHES}]+$`);

export function jobTitleFromQuoteTitle(title: unknown): string {
    const s = String(title ?? '').trim();
    if (!s) return '';
    const m = s.match(AREA_SUFFIX);
    return m && m[1] ? m[1].trim() : s;
}

/** What may sit between a Job's title and a member quote's area. */
// The dashes go first so the hyphen is a literal, not a range.
const MEMBER_TITLE_SEPARATOR = new RegExp(`^[${TITLE_DASHES}\\s:,/|]+`);

/**
 * The part of a quote's title that its Job's title does not already say:
 * "QUOTE RBB/MRM (0926) - Roof" under the Job "RBB/MRM (0926)" gives "Roof".
 * The Run Sheet shows it on a row under a Job caption, where every row would
 * otherwise repeat the Job's title and the column would cut off the one part
 * that differs.
 *
 * Case and runs of spaces are ignored, and so is the constant "QUOTE " prefix
 * on either side. The title must continue with a separator (space, dash, colon,
 * comma, slash or bar) right after the Job's title, so "RBB/MRMX - Roof" is not
 * read as "RBB/MRM" plus "X - Roof". Returns '' when the title does not start
 * with the Job's title, or adds nothing to it.
 */
export function titleWithinJob(title: unknown, jobTitle: unknown): string {
    const tidy = (s: unknown) => String(s ?? '').replace(/\s+/g, ' ').trim();
    const dropPrefix = (s: string) => s.replace(/^QUOTE\s+/i, '');
    const full = tidy(title);
    const job = tidy(jobTitle);
    if (!full || !job) return '';
    for (const [t, j] of [[full, job], [dropPrefix(full), dropPrefix(job)]] as const) {
        if (!j || t.length <= j.length || t.slice(0, j.length).toLowerCase() !== j.toLowerCase()) continue;
        const after = t.slice(j.length);
        if (!MEMBER_TITLE_SEPARATOR.test(after)) continue;
        const rest = after.replace(MEMBER_TITLE_SEPARATOR, '').trim();
        if (rest) return rest;
    }
    return '';
}

/**
 * Put the quotes of one Job together under a caption.
 *
 *  - `sorted` is the sheet as the filters and the sort produced it.
 *  - `pool` is every row the page holds, used only to count the Job's quotes
 *    that the current view leaves out.
 *  - `activeLane` is the lane filter ('all' shows every live lane, never
 *    History).
 *
 * A Job with at least two quotes in `sorted` gets one caption, placed where its
 * first quote falls in the current sort; its other quotes move up to follow in
 * sort order. A Job with one quote in view renders as a plain row with no
 * caption. Rows with no Job keep their exact positions relative to each other.
 *
 * Pure. The output is a permutation of `sorted`: no row is added, dropped or
 * duplicated, which is what keeps counts and selection identical whether the
 * grouping is on or off.
 */
export function groupRunSheetRowsByJob<T extends JobGroupableRow>(
    sorted: T[],
    pool: T[],
    activeLane: string,
): RunSheetDisplay<T> {
    const jobOf = (r: T): string => {
        const id = r?.quoteJobId;
        return typeof id === 'string' ? id.trim() : '';
    };

    const inView = new Map<string, T[]>();
    for (const r of sorted) {
        const jid = jobOf(r);
        if (!jid) continue;
        const list = inView.get(jid);
        if (list) list.push(r); else inView.set(jid, [r]);
    }

    const captions = new Map<string, RunSheetJobCaption>();
    const lastMemberIds = new Set<string>();
    const memberIds = new Set<string>();
    const memberTitles = new Map<string, string>();
    const groups = new Map<string, T[]>();
    for (const [jid, members] of inView) {
        if (members.length >= 2) groups.set(jid, members);
    }
    if (groups.size === 0) return { rows: sorted.slice(), captions, lastMemberIds, memberIds, memberTitles };

    // Every quote of each grouped Job in the whole book, for the "more" counts.
    const shownIds = new Set(sorted.map(r => r.id));
    const outside = new Map<string, T[]>();
    for (const r of pool) {
        const jid = jobOf(r);
        if (!jid || !groups.has(jid) || shownIds.has(r.id)) continue;
        const list = outside.get(jid);
        if (list) list.push(r); else outside.set(jid, [r]);
    }
    // 'all' shows every live lane but never History, so under 'all' only a
    // History quote is "in another lane".
    const inAnotherLane = (r: T) => (activeLane === 'all' ? r.lane === 'history' : r.lane !== activeLane);
    const firstOf = (members: T[], pick: (r: T) => string | undefined): string => {
        for (const m of members) {
            const v = String(pick(m) ?? '').trim();
            if (v) return v;
        }
        return '';
    };

    const rows: T[] = [];
    const emitted = new Set<string>();
    for (const r of sorted) {
        const jid = jobOf(r);
        const members = jid ? groups.get(jid) : undefined;
        if (!members) { rows.push(r); continue; }
        if (emitted.has(jid)) continue;
        emitted.add(jid);
        const leader = members[0]!;
        const hidden = outside.get(jid) || [];
        const inOtherLanes = hidden.filter(inAnotherLane).length;
        const title = firstOf(members, m => m.quoteJobTitle) || jobTitleFromQuoteTitle(leader.title);
        captions.set(leader.id, {
            jobId: jid,
            title,
            qr: firstOf(members, m => m.jobQr) || firstOf(members, m => m.qr),
            sp: firstOf(members, m => m.jobSp) || firstOf(members, m => m.sp),
            count: members.length,
            inOtherLanes,
            hiddenByFilters: hidden.length - inOtherLanes,
        });
        lastMemberIds.add(members[members.length - 1]!.id);
        for (const m of members) {
            rows.push(m);
            memberIds.add(m.id);
            const rest = titleWithinJob(m.title, title);
            if (rest) memberTitles.set(m.id, rest);
        }
    }
    return { rows, captions, lastMemberIds, memberIds, memberTitles };
}
